import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient, { Account, Connection, BudgetStatusResponse, GoalStatusResponse } from '../utils/api';
import { usePlaidLink } from 'react-plaid-link';
import { AIAssistant, AIAssistantHandle } from './AIAssistant';
import ConnectionsManager from './ConnectionsManager';
import FocusGuard from './FocusGuard';
import Sidebar from './Sidebar';
import './Dashboard.css';

interface ChatSession {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
}

const Dashboard: React.FC = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [budgetStatus, setBudgetStatus] = useState<BudgetStatusResponse | null>(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  // Load failure ≠ empty state: a budgets/goals outage must render as
  // "couldn't load", never as "no budgets set up yet".
  const [budgetError, setBudgetError] = useState(false);
  const [goalStatus, setGoalStatus] = useState<GoalStatusResponse | null>(null);
  const [goalLoading, setGoalLoading] = useState(false);
  const [goalError, setGoalError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);

  // Connections manager overlay
  const [showConnectionsManager, setShowConnectionsManager] = useState(false);

  // Plaid fetching splash
  const [plaidFetching, setPlaidFetching] = useState(false);
  const [plaidFetchDone, setPlaidFetchDone] = useState(false);

  // Active content title (updated by sidebar sub-item selection)
  const [activeContentTitle, setActiveContentTitle] = useState('Finn');

  // Sidebar collapse state lives here so the mobile header's hamburger can open
  // it. Default: collapsed (hidden drawer) on mobile, expanded on desktop.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  // Re-sync collapse state when crossing the mobile breakpoint so resizing /
  // rotating never strands the drawer open over the chat (or hidden on desktop).
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setSidebarCollapsed(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Session state
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);

  // Imperative handle into the chat — lets sidebar shortcuts send agent messages
  const assistantRef = useRef<AIAssistantHandle>(null);
  const handleAskAssistant = useCallback((message: string) => {
    assistantRef.current?.sendMessage(message);
  }, []);

  // ── Data loading ──────────────────────────────────────────────────────────
  useEffect(() => {
    loadDashboardData();
    initSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      // Step 1: Load accounts + connections in parallel
      const [accountsRes, connectionsRes] = await Promise.all([
        apiClient.getAccounts(),
        apiClient.getConnections(),
      ]);
      setAccounts(accountsRes.data);
      setConnections(connectionsRes.data?.connections ?? []);
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }

    // Step 2: refresh-all reads access tokens from DB (not Redis),
    // repopulates Redis with both accounts and transactions, then
    // budget status will have real data to read from Redis.
    try {
      await apiClient.refreshAll();
    } catch (_) { /* non-fatal */ }

    // Step 3: Now Redis is populated — budget + goal status will show real data
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    try {
      setBudgetLoading(true);
      const bs = await apiClient.getBudgetStatus(month);
      setBudgetStatus(bs);
      setBudgetError(false);
    } catch (_) { setBudgetError(true); }
    finally { setBudgetLoading(false); }

    try {
      setGoalLoading(true);
      const gs = await apiClient.getGoalStatus(month);
      setGoalStatus(gs);
      setGoalError(false);
    } catch (_) { setGoalError(true); }
    finally { setGoalLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBudgetStatus = useCallback(async () => {
    setBudgetLoading(true);
    try {
      const now = new Date();
      const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const bs = await apiClient.getBudgetStatus(month);
      setBudgetStatus(bs);
      setBudgetError(false);
    } catch (_) { setBudgetError(true); }
    finally { setBudgetLoading(false); }
  }, []);

  const loadGoalStatus = useCallback(async () => {
    setGoalLoading(true);
    try {
      const now = new Date();
      const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const gs = await apiClient.getGoalStatus(month);
      setGoalStatus(gs);
      setGoalError(false);
    } catch (_) { setGoalError(true); }
    finally { setGoalLoading(false); }
  }, []);

  const loadSessions = useCallback(async (): Promise<ChatSession[]> => {
    try {
      const res = await apiClient.listChatSessions();
      const fetched: ChatSession[] = res.data;
      setSessions(fetched);
      return fetched;
    } catch (err) {
      console.error('Failed to load sessions:', err);
      return [];
    }
  }, []);

  const initSessions = async () => {
    let fetched = await loadSessions();
    if (fetched.length === 0) {
      try {
        const res = await apiClient.createChatSession('New Conversation');
        fetched = [res.data];
        setSessions(fetched);
      } catch (err) {
        console.error('Failed to create initial session:', err);
      }
    }
    if (fetched.length > 0) setActiveSessionId(fetched[0].id);
  };

  // ── Session helpers ───────────────────────────────────────────────────────
  const activeSession = sessions.find(s => s.id === activeSessionId);

  const handleNewChat = async () => {
    try {
      const res = await apiClient.createChatSession('New Conversation');
      const newSession: ChatSession = res.data;
      setSessions(prev => [newSession, ...prev]);
      setActiveSessionId(newSession.id);
    } catch (err) {
      console.error('Failed to create session:', err);
    }
  };

  // ── Plaid ─────────────────────────────────────────────────────────────────
  const handlePlaidSuccess = useCallback(async (publicToken: string, metadata: any) => {
    setLinkToken(null);
    setPlaidFetching(true);
    setPlaidFetchDone(false);
    try {
      const response = await apiClient.exchangePlaidToken(publicToken, metadata);
      if (response.data.success) {
        setError('');
        await loadDashboardData();
        setPlaidFetchDone(true);
        setTimeout(() => {
          setPlaidFetching(false);
          setPlaidFetchDone(false);
        }, 1800);
      }
    } catch (err: any) {
      setPlaidFetching(false);
      setPlaidFetchDone(false);
      setError(err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to link bank account');
    }
  }, [loadDashboardData]); // eslint-disable-line react-hooks/exhaustive-deps

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit: (err: any) => {
      setLinkToken(null);
      if (err) setError(err.display_message || err.error_message || 'Bank connection closed with an error.');
    },
  });

  // When linkToken is set and the Plaid SDK finishes loading, open automatically.
  useEffect(() => {
    if (ready && linkToken) open();
  }, [ready, linkToken, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnectBank = async () => {
    // If we already have a token and the SDK is ready, open immediately.
    if (ready && linkToken) { open(); return; }
    // If a token is already being fetched or the SDK is loading, do nothing —
    // the useEffect above will open Plaid as soon as ready flips to true.
    if (linkToken) return;
    try {
      const response = await apiClient.linkPlaidAccount();
      if (response.data?.link_token) setLinkToken(response.data.link_token);
      else setError('Could not get Plaid link token.');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to initiate bank connection');
    }
  };

  const handleLogout = async () => {
    try { await logout(); } catch (err) { console.error('Logout error:', err); }
    navigate('/', { replace: true });
  };

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard">
      <FocusGuard landmarkId="main-content" autoFocusFirst={false} />
      <header className="dashboard-header">
        <button
          className="mobile-menu-btn"
          onClick={() => setSidebarCollapsed(false)}
          aria-label="Open menu"
          aria-expanded={!sidebarCollapsed}
          aria-controls="app-sidebar"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <h1>AskYour<span className="brand-accent">Cash</span></h1>
        <button onClick={handleLogout} className="logout-button" aria-label="Log out of your account">
          Logout
        </button>
      </header>

      {user?.plaid_mode === 'sandbox' && (
        <div className="demo-mode-banner" role="status">
          <span className="demo-mode-tag">Demo mode</span>
          <span>
            You're connected to Plaid's test banks — sign in to "Plaid Bank" with{' '}
            <code>user_good</code> / <code>pass_good</code>. No real accounts are involved.
          </span>
        </div>
      )}

      <main id="main-content" className="dashboard-layout" role="main" aria-label="Main dashboard content">
        {error && (
          <div className="error-banner" role="alert" aria-live="assertive">
            {error}
            <button className="close-button" onClick={() => setError(null)} aria-label="Close error message">×</button>
          </div>
        )}

        {/* ── Left Sidebar (new collapsible accordion with integrated budget) ── */}
        <Sidebar
          accounts={accounts}
          connections={connections}
          sessions={sessions}
          activeSessionId={activeSessionId}
          activeContentTitle={activeContentTitle}
          budgetStatus={budgetStatus}
          budgetLoading={budgetLoading}
          budgetError={budgetError}
          goalStatus={goalStatus}
          goalLoading={goalLoading}
          goalError={goalError}
          collapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
          onSessionSelect={(id) => setActiveSessionId(id)}
          onNewChat={handleNewChat}
          onConnectBank={handleConnectBank}
          onManageConnections={() => setShowConnectionsManager(true)}
          onContentTitleChange={setActiveContentTitle}
          onAskAssistant={handleAskAssistant}
        />

        {/* ── Main Chat Area ── */}
        <section className="dashboard-main">
          <AIAssistant
            ref={assistantRef}
            sessionId={activeSessionId}
            sessionTitle={activeSession?.title || 'Finn'}
            onSessionUpdated={loadSessions}
            onBudgetChanged={loadBudgetStatus}
            onGoalsChanged={loadGoalStatus}
          />
        </section>

        {/* ── Right Panel (removed - integrated into left sidebar) ── */}

      </main>

      {/* Connections Manager Overlay */}
      {showConnectionsManager && (
        <ConnectionsManager
          onClose={() => setShowConnectionsManager(false)}
          onAccountsChanged={loadDashboardData}
        />
      )}

      {/* No Accounts Splash */}
      {!loading && !plaidFetching && accounts.length === 0 && (
        <div className="no-accounts-backdrop">
          <div className="no-accounts-card">
            <div className="no-accounts-icon" aria-hidden="true">🏦</div>
            <h2 className="no-accounts-title">
              {user?.plaid_mode === 'sandbox' ? 'Connect a test bank' : 'Connect your bank account'}
            </h2>
            <p className="no-accounts-sub">
              {user?.plaid_mode === 'sandbox' ? (
                <>
                  Choose "Plaid Bank" and sign in with <code>user_good</code> /{' '}
                  <code>pass_good</code> to load demo accounts and explore FinMind
                  with realistic sample data.
                </>
              ) : (
                <>
                  Link a bank account to start tracking your finances,
                  view balances, and get AI-powered insights.
                </>
              )}
            </p>
            <button
              className="no-accounts-btn"
              onClick={handleConnectBank}
            >
              Add Bank Account
            </button>
            <button
              className="no-accounts-logout"
              onClick={handleLogout}
            >
              Sign out
            </button>
          </div>
        </div>
      )}

      {/* Plaid Fetching Splash */}
      {plaidFetching && (
        <div className="plaid-splash-backdrop">
          <div className="plaid-splash-card">
            {plaidFetchDone ? (
              <>
                <div className="plaid-splash-check">✓</div>
                <h2 className="plaid-splash-title">Connected!</h2>
                <p className="plaid-splash-sub">Your bank account has been linked successfully.</p>
              </>
            ) : (
              <>
                <div className="plaid-splash-spinner" />
                <h2 className="plaid-splash-title">Connecting your bank…</h2>
                <p className="plaid-splash-sub">Fetching your accounts and transactions.<br />This may take a few seconds.</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;